import React, { useState, useEffect } from "react";
import {
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  getDoc,
  startAfter,
  addDoc,
  Timestamp,
  doc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { db } from "../utils/firebase.js";
import { useAuth } from "../hooks/AuthenticationProvider";
import { Helmet } from "react-helmet";
import {
  ClipboardList,
  Search,
  X,
  ChevronDown,
  ListPlus,
  Calendar,
  User,
  Trash2,
  CheckSquare,
  Square,
  MessageSquare,
} from "lucide-react";

const ANALYTICS_FIELDS = {
  isMAT: {
    requiredType: "yesNo",
    options: ["True", "False"],
    displayName: "MAT Status",
    description: "Required for addiction treatment analytics",
  },
  drugOfChoice: {
    requiredType: "multipleChoice",
    options: [
      "THC",
      "Alcohol",
      "Fentanyl",
      "Cocaine",
      "Crack",
      "Meth",
      "Amphetamines",
      "Cough Syrup",
    ],
    maxSelections: 2,
    displayName: "Drug of Choice",
    description: "Required for substance abuse analytics",
  },
  workStatus: {
    requiredType: "dropdown",
    options: ["Unemployed", "Part-Time", "Full-Time", "Retired"],
    displayName: "Work Status",
    description: "Required for employment analytics",
  },
  workOnEntry: {
    requiredType: "dropdown",
    options: ["Unemployed", "Part-Time", "Full-Time", "Retired"],
    displayName: "Work Status on Entry",
    description: "Required for employment analytics",
  },
  veteranStatus: {
    requiredType: "dropdown",
    options: ["Veteran", "No military background"],
    displayName: "Veteran Status",
    description: "Required for veteran analytics",
  },
  gender: {
    requiredType: "dropdown",
    options: ["Male", "Female", "Trans", "Other"],
    displayName: "Gender",
    description: "Required for demographic analytics",
  },
};

const Applications = () => {
  const { organizationId, organizationData, checkPermission, isLoading } =
    useAuth();
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewingFormSubmission, setViewingFormSubmission] = useState(null);
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [noteInput, setNoteInput] = useState("");
  const [editingNoteFor, setEditingNoteFor] = useState(null);

  // For checkbox selection
  const [selectedApplications, setSelectedApplications] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSavingNote, setIsSavingNote] = useState(false);

  // Filter states
  const [dateFilter, setDateFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    if (organizationId) {
      fetchApplications();
    }
  }, [organizationId]);

  // Reset selected applications when applications change
  useEffect(() => {
    setSelectedApplications([]);
    setSelectAll(false);
  }, [applications.length]);

  const fetchApplications = async (startAfterDoc = null) => {
    if (!organizationId) return;

    try {
      setLoading(true);
      let applicationsRef = collection(
        db,
        `organizations/${organizationId}/applications`
      );

      let q = query(
        applicationsRef,
        orderBy("dateSubmitted", "desc"),
        limit(10)
      );

      if (startAfterDoc) {
        q = query(
          applicationsRef,
          orderBy("dateSubmitted", "desc"),
          startAfter(startAfterDoc),
          limit(10)
        );
      }

      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setHasMore(false);
        setLoading(false);
        return;
      }

      const lastDoc = querySnapshot.docs[querySnapshot.docs.length - 1];
      setLastVisible(lastDoc);

      const newApplications = querySnapshot.docs.map((doc) => {
        const data = doc.data();

        // Try to extract applicant name for each application
        let applicantName = data.applicantName;

        // Look for applicant name directly in the responses object
        if (!applicantName && data.responses) {
          // First try looking for a field with "applicantName" prefix in key
          const applicantNameKey = Object.keys(data.responses).find((key) =>
            key.startsWith("applicantName-")
          );

          if (applicantNameKey) {
            applicantName = data.responses[applicantNameKey];
          }
        }

        // If still not found, try the previous approach as fallback
        if (!applicantName && data.responses && data.responses.responses) {
          // Try to find by isApplicantIdentifier flag
          const applicantFieldId = Object.keys(
            data.responses.elementMetadata || {}
          ).find(
            (key) => data.responses.elementMetadata[key].isApplicantIdentifier
          );

          if (applicantFieldId && data.responses.responses[applicantFieldId]) {
            applicantName = data.responses.responses[applicantFieldId];
          }
        }

        return {
          id: doc.id,
          ...data,
          applicantName: applicantName || null,
          note: data.note || "", // Add the note field
        };
      });

      if (startAfterDoc) {
        setApplications((prev) => [...prev, ...newApplications]);
      } else {
        setApplications(newApplications);
      }

      setHasMore(querySnapshot.docs.length === 10);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching applications:", error);
      setLoading(false);
    }
  };

  const normalizeAnalyticsValue = (fieldKey, value) => {
    if (!ANALYTICS_FIELDS[fieldKey]) return value;

    const field = ANALYTICS_FIELDS[fieldKey];

    switch (fieldKey) {
      case "isMAT":
        // Convert "True"/"False" strings to actual booleans
        if (typeof value === "string") {
          return value.toLowerCase() === "true";
        }
        return Boolean(value);

      case "drugOfChoice":
        // Handle multiple selections - ensure it's an array and limit to 2
        if (Array.isArray(value)) {
          return value.slice(0, field.maxSelections);
        }
        if (typeof value === "string") {
          // Split by comma if it's a comma-separated string
          const choices = value
            .split(",")
            .map((c) => c.trim())
            .filter(Boolean);
          return choices.slice(0, field.maxSelections);
        }
        return [value].filter(Boolean);

      case "workStatus":
      case "workOnEntry":
        // Ensure the value matches expected options
        if (field.options.includes(value)) {
          return value;
        }
        // Try to normalize common variations
        const lowerValue = String(value).toLowerCase();
        if (
          lowerValue.includes("unemployed") ||
          lowerValue.includes("not working")
        ) {
          return "Unemployed";
        }
        if (
          lowerValue.includes("part-time") ||
          lowerValue.includes("part time")
        ) {
          return "Part-Time";
        }
        if (
          lowerValue.includes("full-time") ||
          lowerValue.includes("full time")
        ) {
          return "Full-Time";
        }
        if (lowerValue.includes("retired")) {
          return "Retired";
        }
        return "";

      case "veteranStatus":
        // Convert various formats to standard options
        if (typeof value === "boolean") {
          return value ? "Veteran" : "No military background";
        }
        const lowerVet = String(value).toLowerCase();
        if (
          lowerVet === "yes" ||
          lowerVet === "veteran" ||
          lowerVet === "true"
        ) {
          return "Veteran";
        }
        if (
          lowerVet === "no" ||
          lowerVet === "false" ||
          lowerVet.includes("no military")
        ) {
          return "No military background";
        }
        return "";

      case "gender":
        // Ensure gender matches expected options
        if (field.options.includes(value)) {
          return value;
        }
        const lowerGender = String(value).toLowerCase();
        if (lowerGender.includes("male") && !lowerGender.includes("female")) {
          return "Male";
        }
        if (lowerGender.includes("female")) {
          return "Female";
        }
        if (lowerGender.includes("trans")) {
          return "Trans";
        }
        return "Other";

      default:
        return value;
    }
  };

  const moveToWaitlist = async (application) => {
    try {
      // First, map the application data to the waitlist/resident structure
      const waitlistData = {};

      console.log("Application data:", application); // Debug log to see full data structure

      // Get all responses - handle both structure formats
      const responses =
        application.responses?.responses || application.responses || {};

      // Debug log the responses
      console.log("All available responses:", responses);

      // NEW: Analytics field processing with smart lookup
      const processAnalyticsField = (fieldKey) => {
        // Look for the field in responses using multiple strategies
        const possibleKeys = Object.keys(responses).filter(
          (key) =>
            key.toLowerCase().includes(fieldKey.toLowerCase()) ||
            key === fieldKey ||
            (responses[key] &&
              typeof responses[key] === "object" &&
              responses[key].mapToIntake === fieldKey)
        );

        if (possibleKeys.length > 0) {
          const rawValue = responses[possibleKeys[0]];
          const normalizedValue = normalizeAnalyticsValue(fieldKey, rawValue);
          console.log(
            `Analytics field ${fieldKey}: ${rawValue} -> ${normalizedValue}`
          );
          return normalizedValue;
        }

        return null;
      };

      // Process analytics fields first
      Object.keys(ANALYTICS_FIELDS).forEach((fieldKey) => {
        const value = processAnalyticsField(fieldKey);
        if (value !== null) {
          waitlistData[fieldKey] = value;
        }
      });

      // Direct mapping for critical fields
      // 1. Handle DOB (date of birth)
      if (responses["date-1741089463847"] || responses["dob"]) {
        const dobData = responses["date-1741089463847"] || responses["dob"];
        if (dobData && dobData.seconds) {
          // Use the Timestamp constructor directly
          waitlistData.dob = new Timestamp(
            dobData.seconds,
            dobData.nanoseconds || 0
          );
        } else if (dobData) {
          try {
            const dateObj = new Date(dobData);
            if (!isNaN(dateObj.getTime())) {
              waitlistData.dob = Timestamp.fromDate(dateObj);
            }
          } catch (error) {
            console.warn("Error processing DOB:", error);
          }
        }
      }

      // 2. Handle name (use the mapped field that worked)
      if (responses["applicantName-1741179238918"] || responses["fullName"]) {
        const fullName =
          responses["applicantName-1741179238918"] ||
          responses["fullName"] ||
          "";
        const nameParts = fullName.trim().split(" ");

        // Logic for name splitting
        if (nameParts.length === 1) {
          waitlistData.firstName = nameParts[0];
          waitlistData.lastName = nameParts[0];
        } else if (nameParts.length === 2) {
          waitlistData.firstName = nameParts[0];
          waitlistData.lastName = nameParts[1];
        } else if (nameParts.length > 2) {
          waitlistData.firstName = nameParts.slice(0, -1).join(" ");
          waitlistData.lastName = nameParts[nameParts.length - 1];
        } else {
          waitlistData.firstName = "";
          waitlistData.lastName = "";
        }

        waitlistData.fullName = fullName;
      }

      // 3. Handle phone number
      if (responses["phone-1741089455578"] || responses["phoneNumber"]) {
        waitlistData.phoneNumber =
          responses["phone-1741089455578"] || responses["phoneNumber"] || "";
      }

      // 4. Handle emergency contact info
      if (responses["input-1741089548499"] || responses["ecFirstName"]) {
        // Handle EC name differently since it's not split in the form
        const ecName =
          responses["input-1741089548499"] || responses["ecFirstName"] || "";
        const ecNameParts = ecName.trim().split(" ");

        if (ecNameParts.length === 1) {
          waitlistData.ecFirstName = ecNameParts[0];
          waitlistData.ecLastName = "";
        } else if (ecNameParts.length >= 2) {
          waitlistData.ecFirstName = ecNameParts[0];
          waitlistData.ecLastName = ecNameParts.slice(1).join(" ");
        } else {
          waitlistData.ecFirstName = "";
          waitlistData.ecLastName = "";
        }

        // Set relationship if present
        waitlistData.ecRelationship = responses["ecRelationship"] || "";
      }

      // 5. Handle EC phone
      if (responses["phone-1741089569098"] || responses["ecPhoneNumber"]) {
        waitlistData.ecPhoneNumber =
          responses["phone-1741089569098"] || responses["ecPhoneNumber"] || "";
      }

      // 6. Handle Sobriety Date
      if (responses["date-1741089970165"] || responses["sobrietyDate"]) {
        const sobrietyData =
          responses["date-1741089970165"] || responses["sobrietyDate"];
        if (sobrietyData && sobrietyData.seconds) {
          // Use the Timestamp constructor directly
          waitlistData.sobrietyDate = new Timestamp(
            sobrietyData.seconds,
            sobrietyData.nanoseconds || 0
          );
        } else if (sobrietyData) {
          try {
            const dateObj = new Date(sobrietyData);
            if (!isNaN(dateObj.getTime())) {
              waitlistData.sobrietyDate = Timestamp.fromDate(dateObj);
            }
          } catch (error) {
            console.warn("Error processing sobriety date:", error);
          }
        }
      }

      // 7. Handle intake date
      if (responses["date-1741089835168"] || responses["intakeDate"]) {
        const intakeDateData =
          responses["date-1741089835168"] || responses["intakeDate"];
        if (intakeDateData && intakeDateData.seconds) {
          waitlistData.intakeDate = Timestamp.fromMillis(
            intakeDateData.seconds * 1000
          );
        } else if (intakeDateData) {
          try {
            const dateObj = new Date(intakeDateData);
            if (!isNaN(dateObj.getTime())) {
              waitlistData.intakeDate = Timestamp.fromDate(dateObj);
            }
          } catch (error) {
            console.warn("Error processing intake date:", error);
          }
        }
      }

      // 8. UPDATED: Handle MAT status with fallback to old logic
      if (!waitlistData.hasOwnProperty("isMAT")) {
        if (
          responses["input-1741090294950"] !== undefined ||
          responses["isMAT"] !== undefined
        ) {
          const matValue =
            responses["input-1741090294950"] || responses["isMAT"];

          // Handle different possible formats
          if (typeof matValue === "boolean") {
            // If it's already a boolean, use it directly
            waitlistData.isMAT = matValue;
          } else if (typeof matValue === "string") {
            // Convert "True"/"False" to boolean, plus legacy "Yes/No" handling
            if (matValue.toLowerCase() === "true") {
              waitlistData.isMAT = true;
            } else if (matValue.toLowerCase() === "false") {
              waitlistData.isMAT = false;
            } else {
              // Legacy handling for "Yes"/"No" and medication names
              waitlistData.isMAT =
                matValue.toLowerCase() === "yes" ||
                matValue.toLowerCase().includes("yes") ||
                matValue.toLowerCase().includes("suboxone") ||
                matValue.toLowerCase().includes("methadone") ||
                matValue.toLowerCase().includes("vivitrol") ||
                matValue.toLowerCase().includes("sublocade") ||
                matValue.toLowerCase().includes("buprenorphine");
            }
          } else {
            // Default value if unable to determine
            waitlistData.isMAT = false;
          }
        }
      }

      // 9. UPDATED: Handle Veteran Status with fallback to old logic
      if (!waitlistData.hasOwnProperty("veteranStatus")) {
        if (
          responses["yesNo-1741463082220"] !== undefined ||
          responses["veteranStatus"] !== undefined
        ) {
          const vetValue =
            responses["yesNo-1741463082220"] !== undefined
              ? responses["yesNo-1741463082220"]
              : responses["veteranStatus"];

          // Convert to standardized string for dropdown
          if (typeof vetValue === "boolean") {
            // true means Veteran, false means No military background
            waitlistData.veteranStatus =
              vetValue === true ? "Veteran" : "No military background";
          } else if (typeof vetValue === "string") {
            // Handle string values
            if (
              vetValue.toLowerCase() === "yes" ||
              vetValue.toLowerCase() === "veteran" ||
              vetValue.toLowerCase() === "true"
            ) {
              waitlistData.veteranStatus = "Veteran";
            } else if (
              vetValue.toLowerCase() === "no" ||
              vetValue.toLowerCase() === "non-veteran" ||
              vetValue.toLowerCase() === "false" ||
              vetValue.toLowerCase() === "no military background"
            ) {
              waitlistData.veteranStatus = "No military background";
            } else {
              // Default value if string doesn't match expected values
              waitlistData.veteranStatus = "";
            }
          } else {
            // Default value if unable to determine
            waitlistData.veteranStatus = "";
          }
        }
      }

      // 10. Handle address (not in your fields but often useful)
      if (responses["input-1741089489142"]) {
        waitlistData.address = responses["input-1741089489142"];
      }

      // 11. Handle medications
      if (responses["input-1741090298733"] || responses["medications"]) {
        const medsText =
          responses["input-1741090298733"] || responses["medications"] || "";

        // Try to parse medication list from text
        if (typeof medsText === "string" && medsText.trim() !== "") {
          // Split on commas to separate medications
          const medList = medsText
            .split(",")
            .map((med) => med.trim())
            .filter(Boolean);

          waitlistData.medications = medList.map((med) => {
            const parts = med.split(",");
            // Try to extract name and dose with regex
            const match = med.match(
              /^(.*?)(?:\s*,\s*|\s+)(\d+\s*mg|\d+\s*mcg|\d+\s*ml|\d+.*)?$/i
            );

            if (match) {
              return { name: match[1].trim(), dose: (match[2] || "").trim() };
            }

            return { name: med.trim(), dose: "" };
          });
        } else if (Array.isArray(medsText)) {
          waitlistData.medications = medsText;
        } else {
          waitlistData.medications = [{ name: "", dose: "" }];
        }
      }

      // 12. UPDATED: Handle drug of choice with fallback to old logic
      if (!waitlistData.hasOwnProperty("drugOfChoice")) {
        // Look for fields that might contain drug of choice information
        const drugKeys = Object.keys(responses).filter(
          (key) =>
            key.toLowerCase().includes("drug") ||
            key.toLowerCase().includes("doc") ||
            key.toLowerCase().includes("substance")
        );

        if (drugKeys.length > 0) {
          const rawValue = responses[drugKeys[0]] || "";
          // Handle both string and array formats
          if (Array.isArray(rawValue)) {
            waitlistData.drugOfChoice = rawValue.slice(0, 2); // Limit to 2
          } else if (typeof rawValue === "string") {
            waitlistData.drugOfChoice = rawValue
              .split(",")
              .map((c) => c.trim())
              .filter(Boolean)
              .slice(0, 2);
          } else {
            waitlistData.drugOfChoice = rawValue;
          }
        }
      }

      // 13. UPDATED: Handle work status with fallback to old logic
      if (!waitlistData.hasOwnProperty("workStatus")) {
        if (responses["input-1741090586014"]) {
          const workText = responses["input-1741090586014"] || "";
          if (
            workText.toLowerCase().includes("not working") ||
            workText.toLowerCase().includes("unemployed")
          ) {
            waitlistData.workStatus = "Unemployed";
          } else if (
            workText.toLowerCase().includes("part time") ||
            workText.toLowerCase().includes("part-time")
          ) {
            waitlistData.workStatus = "Part-Time";
          } else if (
            workText.toLowerCase().includes("full time") ||
            workText.toLowerCase().includes("full-time")
          ) {
            waitlistData.workStatus = "Full-Time";
          } else {
            waitlistData.workStatus = workText.trim() !== "" ? "Full-Time" : "";
          }
        }
      }

      // 13b. UPDATED: Handle workOnEntry with fallback
      if (!waitlistData.hasOwnProperty("workOnEntry")) {
        // Since workOnEntry is new, we'll initially populate it with the same value as workStatus
        if (waitlistData.workStatus) {
          waitlistData.workOnEntry = waitlistData.workStatus;
        } else if (responses["workOnEntry"]) {
          // If there's a direct workOnEntry field, use that
          waitlistData.workOnEntry = responses["workOnEntry"];
        }
      }

      // 14. UPDATED: Handle gender with fallback to old logic
      if (!waitlistData.hasOwnProperty("gender")) {
        // look in the responses for anything that might indicate gender
        const genderKeys = Object.keys(responses).filter(
          (key) =>
            key.toLowerCase().includes("gender") ||
            key.toLowerCase().includes("sex")
        );

        if (genderKeys.length > 0) {
          const genderValue = responses[genderKeys[0]];
          if (genderValue) {
            if (typeof genderValue === "string") {
              const lowerGender = genderValue.toLowerCase();
              if (
                lowerGender.includes("male") &&
                !lowerGender.includes("female")
              ) {
                waitlistData.gender = "Male";
              } else if (lowerGender.includes("female")) {
                waitlistData.gender = "Female";
              } else if (lowerGender.includes("trans")) {
                waitlistData.gender = "Trans";
              } else {
                waitlistData.gender = "Other";
              }
            } else {
              waitlistData.gender = genderValue;
            }
          }
        }
      }

      // 15. Handle pending charges
      if (responses["input-1741089973491"]) {
        waitlistData.pendingCharges = responses["input-1741089973491"];
      }

      // Get organization data for default rent settings
      const houseSettingsRef = doc(
        db,
        `organizations/${organizationId}/houseSettings/initial`
      );
      const houseSettingsSnap = await getDoc(houseSettingsRef);
      const houseSettings = houseSettingsSnap.exists()
        ? houseSettingsSnap.data()
        : {};

      // Add all default fields from initialState
      const completeWaitlistData = {
        // Start with the mapped data
        ...waitlistData,

        // Essential intake form fields with defaults
        firstName: waitlistData.firstName || "",
        lastName: waitlistData.lastName || "",
        fullName: waitlistData.fullName || "",
        dob: waitlistData.dob || null,
        phoneNumber: waitlistData.phoneNumber || "",

        // Housing fields - CRITICAL for balance calculation
        house: waitlistData.house || "",
        rentAmount: waitlistData.rentAmount || 0,
        rentSchedule:
          waitlistData.rentSchedule || houseSettings.rentSchedule || "weekly",
        rentDueDate: waitlistData.rentDueDate || null, // Keep as null, will be set during move-in
        intakeDate: waitlistData.intakeDate || null, // Keep as null, will be set during move-in

        workOnEntry: waitlistData.workOnEntry || "",
        employmentHistory: waitlistData.employmentHistory || [],

        // Contact information
        email: waitlistData.email || "",
        workStatus: waitlistData.workStatus || "",
        gender: waitlistData.gender || "",
        veteranStatus: waitlistData.veteranStatus || "",
        phase: waitlistData.phase || "",

        // Emergency contact
        ecFirstName: waitlistData.ecFirstName || "",
        ecLastName: waitlistData.ecLastName || "",
        ecRelationship: waitlistData.ecRelationship || "",
        ecPhoneNumber: waitlistData.ecPhoneNumber || "",
        ecEmail: waitlistData.ecEmail || "",

        // Medical information - UPDATED with proper analytics field handling
        medications: waitlistData.medications || [{ name: "", dose: "" }],
        drugOfChoice: waitlistData.drugOfChoice || "",
        sobrietyDate: waitlistData.sobrietyDate || null,
        isMAT: waitlistData.isMAT !== undefined ? waitlistData.isMAT : false,

        // Legal information
        referralLocation: waitlistData.referralLocation || "",
        poFirstName: waitlistData.poFirstName || "",
        poLastName: waitlistData.poLastName || "",
        poEmail: waitlistData.poEmail || "",
        county: waitlistData.county || "",
        pendingCharges: waitlistData.pendingCharges || "",

        // Resident management fields - these are critical for the balance calculation
        balance: 0,
        currentResident: false,
        bedLocation: "",
        uaCount: 3,

        // Waitlist specific fields
        isWaitlist: true,
        dateAdded: Timestamp.now(),
        status: "pending-bed",

        // 90n90 and meeting requirements (important for balance calculation)
        is90n90: false,
        meetingRequirements: 0,
      };

      console.log(
        "Final waitlist data with analytics normalization:",
        completeWaitlistData
      );

      // Create the waitlist document
      const waitlistRef = await addDoc(
        collection(db, `organizations/${organizationId}/waitlist`),
        completeWaitlistData
      );

      // Update application status to approved
      await updateApplicationStatus(application.id, "approved");

      // Update local state to reflect changes
      setApplications((prev) =>
        prev.map((app) =>
          app.id === application.id ? { ...app, status: "approved" } : app
        )
      );

      // Close the modal
      setViewingFormSubmission(null);

      alert("Application approved and moved to waitlist successfully!");
      navigate("/waitlist");
    } catch (error) {
      console.error("Error moving application to waitlist:", error);
      alert("There was an error moving the application to waitlist.");
    }
  };

  const loadMore = () => {
    if (lastVisible) {
      fetchApplications(lastVisible);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredApplications = applications.filter((app) => {
    // First apply status filter
    if (statusFilter !== "all" && app.status !== statusFilter) {
      return false;
    }

    // If search term is empty, just apply date filter
    if (!searchTerm.trim()) {
      return applyDateFilter(app);
    }

    // Normalize search terms to case-insensitive
    const searchLower = searchTerm.toLowerCase().trim();

    // Check for applicant name match - prioritize this
    const nameMatch = app.applicantName
      ? app.applicantName.toLowerCase().includes(searchLower)
      : false;

    // If name matches, apply date filter
    if (nameMatch) {
      return applyDateFilter(app);
    }

    // Secondary search criteria (fallback if no direct name match)
    const emailMatch = app.submittedBy?.email
      ? app.submittedBy.email.toLowerCase().includes(searchLower)
      : false;

    const noteMatch = app.note
      ? app.note.toLowerCase().includes(searchLower)
      : false;

    // Apply date filter to secondary matches
    return (emailMatch || noteMatch) && applyDateFilter(app);
  });

  function applyDateFilter(app) {
    if (dateFilter === "all") return true;

    const appDate = app.dateSubmitted
      ? new Date(app.dateSubmitted.seconds * 1000)
      : null;

    if (!appDate) return false;

    const now = new Date();
    const daysDiff = Math.floor((now - appDate) / (1000 * 60 * 60 * 24));

    switch (dateFilter) {
      case "today":
        return daysDiff < 1;
      case "week":
        return daysDiff < 7;
      case "month":
        return daysDiff < 30;
      default:
        return true;
    }
  }

  const formatDate = (timestamp) => {
    if (!timestamp) return "Unknown date";

    const date = new Date(timestamp.seconds * 1000);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Handle checkbox selection
  const toggleSelectApplication = (e, applicationId) => {
    e.stopPropagation(); // Prevent opening modal

    setSelectedApplications((prev) => {
      if (prev.includes(applicationId)) {
        return prev.filter((id) => id !== applicationId);
      } else {
        return [...prev, applicationId];
      }
    });
  };

  // Handle select all checkbox
  const toggleSelectAll = (e) => {
    e.stopPropagation();

    if (selectAll) {
      setSelectedApplications([]);
    } else {
      setSelectedApplications(filteredApplications.map((app) => app.id));
    }
    setSelectAll(!selectAll);
  };

  // Handle delete selected applications
  const deleteSelectedApplications = async () => {
    if (selectedApplications.length === 0) return;

    if (
      !window.confirm(
        `Are you sure you want to delete ${selectedApplications.length} selected application(s)?`
      )
    ) {
      return;
    }

    setIsDeleting(true);

    try {
      for (const appId of selectedApplications) {
        const applicationRef = doc(
          db,
          `organizations/${organizationId}/applications/${appId}`
        );
        await deleteDoc(applicationRef);
      }

      // Update local state after successful deletion
      setApplications((prev) =>
        prev.filter((app) => !selectedApplications.includes(app.id))
      );

      // Reset selections
      setSelectedApplications([]);
      setSelectAll(false);
    } catch (error) {
      console.error("Error deleting applications:", error);
      alert("There was an error deleting the selected applications.");
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle note editing
  const startEditingNote = (applicationId, currentNote) => {
    setEditingNoteFor(applicationId);
    setNoteInput(currentNote || "");
  };

  // Handle note saving
  const saveNote = async (applicationId) => {
    if (!applicationId) return;

    setIsSavingNote(true);
    try {
      const applicationRef = doc(
        db,
        `organizations/${organizationId}/applications/${applicationId}`
      );

      // Update in Firestore
      await updateDoc(applicationRef, { note: noteInput });

      // Update in local state
      setApplications((prev) =>
        prev.map((app) =>
          app.id === applicationId ? { ...app, note: noteInput } : app
        )
      );

      // Reset editing state
      setEditingNoteFor(null);
      setNoteInput("");
    } catch (error) {
      console.error("Error saving note:", error);
      alert("There was an error saving the note.");
    } finally {
      setIsSavingNote(false);
    }
  };

  const cancelEditingNote = () => {
    setEditingNoteFor(null);
    setNoteInput("");
  };

  // Update application status
  const updateApplicationStatus = async (id, status) => {
    try {
      const applicationRef = doc(
        db,
        `organizations/${organizationId}/applications/${id}`
      );
      await updateDoc(applicationRef, { status });

      // Update local state
      setApplications((prev) =>
        prev.map((app) => (app.id === id ? { ...app, status } : app))
      );
    } catch (error) {
      console.error("Error updating application status:", error);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0F1E] p-6 relative overflow-hidden">
      <Helmet>
        <title>Applications | {organizationData?.name || ""}</title>
      </Helmet>

      {/* Base gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#0A0F1E] via-[#0A0F1E] to-[#0C1222]" />

      {/* Very subtle pattern overlay */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(24,24,27,0.3),rgba(0,0,0,0))]" />

      <div className="absolute inset-0">
        <div className="absolute top-0 -left-4 w-96 h-96 bg-cyan-500/10 rounded-full mix-blend-screen filter blur-3xl animate-pulse" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full mix-blend-screen filter blur-3xl animate-pulse delay-700" />
      </div>

      <div className="max-w-7xl mx-auto space-y-8 relative">
        {/* Header */}
        <div className="flex items-center gap-6 mb-12">
          <div className="flex gap-3">
            <div className="h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse" />
            <div className="h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse delay-100 opacity-70" />
            <div className="h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse delay-200 opacity-40" />
          </div>
          <div>
            <h1 className="text-4xl font-bold text-white tracking-tight">
              Applications
            </h1>
          </div>
        </div>

        {/* Search and Filters Bar */}
        <div className="bg-slate-900/70 border border-slate-800 rounded-xl p-4 mb-6 backdrop-blur-sm">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="relative flex-grow max-w-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search size={18} className="text-slate-400" />
              </div>
              <input
                type="text"
                placeholder="Search by applicant name or notes..."
                className="pl-10 pr-4 py-2 w-full bg-slate-800/50 border border-slate-700 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 text-white"
                value={searchTerm}
                onChange={handleSearch}
              />
            </div>

            {/* Filter by status */}
            <div className="flex items-center gap-2">
              <span className="text-slate-400 text-sm whitespace-nowrap">
                Status:
              </span>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="bg-slate-800/50 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            {/* Delete button (only shows when applications are selected) */}
            {selectedApplications.length > 0 && (
              <div className="flex items-center">
                <button
                  onClick={deleteSelectedApplications}
                  disabled={isDeleting}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600/20 text-red-400 hover:bg-red-600/30 rounded-lg transition-colors"
                >
                  <Trash2 size={16} />
                  {isDeleting
                    ? "Deleting..."
                    : `Delete (${selectedApplications.length})`}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Applications List */}
        <div className="bg-slate-900/70 border border-slate-800 rounded-xl backdrop-blur-sm overflow-hidden">
          {loading && applications.length === 0 ? (
            <div className="flex justify-center items-center h-64">
              <div className="text-slate-400 animate-pulse">
                Loading applications...
              </div>
            </div>
          ) : filteredApplications.length === 0 ? (
            <div className="flex flex-col justify-center items-center h-64">
              <ClipboardList size={48} className="text-slate-600 mb-4" />
              <h3 className="text-slate-400 text-lg">No applications found</h3>
              <p className="text-slate-500">
                Try adjusting your search or filters
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left border-b border-slate-800">
                      <th className="p-4 text-slate-400 font-medium w-12">
                        <div
                          className="flex items-center cursor-pointer"
                          onClick={toggleSelectAll}
                        >
                          {selectAll ? (
                            <CheckSquare size={18} className="text-blue-400" />
                          ) : (
                            <Square size={18} className="text-slate-500" />
                          )}
                        </div>
                      </th>
                      <th className="p-4 text-slate-400 font-medium w-48">
                        Applicant
                      </th>
                      <th className="p-4 text-slate-400 font-medium w-40">
                        Date Submitted
                      </th>
                      <th className="p-4 text-slate-400 font-medium w-32">
                        Status
                      </th>
                      <th className="p-4 text-slate-400 font-medium w-96 max-w-96">
                        Notes
                      </th>
                      <th className="p-4 text-slate-400 font-medium sr-only w-12">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredApplications.map((application) => (
                      <tr
                        key={application.id}
                        className={`border-b border-slate-800 hover:bg-slate-800/50 transition-colors ${
                          selectedApplications.includes(application.id)
                            ? "bg-slate-800/30"
                            : ""
                        }`}
                      >
                        <td className="p-4 w-12">
                          <div
                            className="cursor-pointer"
                            onClick={(e) =>
                              toggleSelectApplication(e, application.id)
                            }
                          >
                            {selectedApplications.includes(application.id) ? (
                              <CheckSquare
                                size={18}
                                className="text-blue-400"
                              />
                            ) : (
                              <Square size={18} className="text-slate-500" />
                            )}
                          </div>
                        </td>
                        <td
                          className="p-4 w-48 cursor-pointer"
                          onClick={() => setViewingFormSubmission(application)}
                        >
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-500/10 rounded-lg">
                              <User className="text-blue-400" size={16} />
                            </div>
                            <span className="font-medium text-white">
                              {application.applicantName || "Unknown Applicant"}
                            </span>
                          </div>
                        </td>
                        <td
                          className="p-4 w-40 cursor-pointer"
                          onClick={() => setViewingFormSubmission(application)}
                        >
                          <div className="flex items-center gap-2">
                            <Calendar size={16} className="text-slate-400" />
                            <span className="text-slate-300">
                              {application.dateSubmitted
                                ? formatDate(application.dateSubmitted)
                                : "Unknown date"}
                            </span>
                          </div>
                        </td>
                        <td className="p-4 w-32">
                          <div className="relative">
                            <select
                              value={application.status || "pending"}
                              onChange={(e) => {
                                e.stopPropagation(); // Prevent opening the modal
                                const newStatus = e.target.value;
                                updateApplicationStatus(
                                  application.id,
                                  newStatus
                                );

                                // If status changes to rejected and there's no note, start editing
                                if (
                                  newStatus === "rejected" &&
                                  !application.note
                                ) {
                                  startEditingNote(application.id, "");
                                }
                              }}
                              className={`w-full px-2 py-1 rounded-full text-xs font-medium bg-slate-800 border border-slate-700 appearance-none cursor-pointer
                              ${
                                application.status === "approved"
                                  ? "text-green-400"
                                  : application.status === "rejected"
                                  ? "text-red-400"
                                  : "text-yellow-400"
                              }`}
                              onClick={(e) => e.stopPropagation()} // Prevent opening the modal when clicking
                            >
                              <option
                                value="pending"
                                className="bg-slate-800 text-yellow-400"
                              >
                                Pending
                              </option>
                              <option
                                value="approved"
                                className="bg-slate-800 text-green-400"
                              >
                                Approved
                              </option>
                              <option
                                value="rejected"
                                className="bg-slate-800 text-red-400"
                              >
                                Rejected
                              </option>
                            </select>
                          </div>
                        </td>
                        <td className="p-4 w-96 max-w-96 overflow-hidden">
                          {editingNoteFor === application.id ? (
                            <div className="flex flex-col gap-2">
                              <textarea
                                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm resize-none focus:outline-none focus:ring-1 focus:ring-blue-500"
                                value={noteInput}
                                onChange={(e) => setNoteInput(e.target.value)}
                                placeholder="Add a note..."
                                rows={2}
                                onClick={(e) => e.stopPropagation()}
                              />
                              <div className="flex gap-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    saveNote(application.id);
                                  }}
                                  disabled={isSavingNote}
                                  className="px-3 py-1 bg-blue-600/20 text-blue-400 hover:bg-blue-600/30 rounded text-xs"
                                >
                                  {isSavingNote ? "Saving..." : "Save"}
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    cancelEditingNote();
                                  }}
                                  className="px-3 py-1 bg-slate-700/20 text-slate-400 hover:bg-slate-700/30 rounded text-xs"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center justify-between gap-2">
                              <div
                                className="text-slate-300 line-clamp-2 flex-grow cursor-pointer truncate"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setViewingFormSubmission(application);
                                }}
                              >
                                {application.note || (
                                  <span className="text-slate-500 italic">
                                    No notes
                                  </span>
                                )}
                              </div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  startEditingNote(
                                    application.id,
                                    application.note
                                  );
                                }}
                                className="p-1 hover:bg-slate-800 rounded transition-colors text-slate-400 hover:text-slate-300"
                              >
                                <MessageSquare size={14} />
                              </button>
                            </div>
                          )}
                        </td>
                        <td
                          className="p-4 text-right cursor-pointer w-12"
                          onClick={() => setViewingFormSubmission(application)}
                        >
                          <ChevronDown size={16} className="text-slate-400" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {hasMore && (
                <div className="p-4 border-t border-slate-800 flex justify-center">
                  <button
                    onClick={loadMore}
                    disabled={loading}
                    className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg flex items-center gap-2 transition-colors"
                  >
                    {loading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-slate-500 border-t-slate-300 rounded-full animate-spin" />
                        Loading...
                      </>
                    ) : (
                      <>
                        <ChevronDown size={16} />
                        Load More
                      </>
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Form Submission View Modal */}
      {viewingFormSubmission && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex justify-center items-center overflow-auto">
          <div className="bg-slate-900/90 border border-slate-700 rounded-xl shadow-lg w-full max-w-2xl m-4">
            <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/10 rounded-lg">
                  <User className="text-blue-400" size={20} />
                </div>
                <h3 className="text-xl font-semibold text-white">
                  {viewingFormSubmission.applicantName || "Unknown Applicant"}
                </h3>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setViewingFormSubmission(null)}
                  className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
                >
                  <X
                    className="text-slate-400 hover:text-slate-300"
                    size={20}
                  />
                </button>
              </div>
            </div>

            {/* Application Notes Section */}
            <div className="px-6 py-4 border-b border-slate-700">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-lg font-medium text-white">Notes</h4>
                {editingNoteFor !== viewingFormSubmission.id && (
                  <button
                    onClick={() =>
                      startEditingNote(
                        viewingFormSubmission.id,
                        viewingFormSubmission.note
                      )
                    }
                    className="px-3 py-1 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded text-sm flex items-center gap-1"
                  >
                    <MessageSquare size={14} />
                    {viewingFormSubmission.note ? "Edit Note" : "Add Note"}
                  </button>
                )}
              </div>

              {editingNoteFor === viewingFormSubmission.id ? (
                <div className="flex flex-col gap-2">
                  <textarea
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm resize-none focus:outline-none focus:ring-1 focus:ring-blue-500"
                    value={noteInput}
                    onChange={(e) => setNoteInput(e.target.value)}
                    placeholder="Add a note about this application..."
                    rows={3}
                  />
                  <div className="flex gap-2 justify-end">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        cancelEditingNote();
                      }}
                      className="px-3 py-1 bg-slate-700 text-slate-300 hover:bg-slate-600 rounded text-sm"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => saveNote(viewingFormSubmission.id)}
                      disabled={isSavingNote}
                      className="px-3 py-1 bg-blue-600 text-white hover:bg-blue-500 rounded text-sm"
                    >
                      {isSavingNote ? "Saving..." : "Save Note"}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="p-3 bg-slate-800/40 rounded-lg">
                  {viewingFormSubmission.note ? (
                    <p className="text-slate-300 whitespace-pre-wrap">
                      {viewingFormSubmission.note}
                    </p>
                  ) : (
                    <p className="text-slate-500 italic">No notes added yet.</p>
                  )}
                </div>
              )}

              {/* Status display and update */}
              <div className="mt-4">
                <h4 className="text-sm font-medium text-slate-400 mb-2">
                  Status
                </h4>
                <select
                  value={viewingFormSubmission.status || "pending"}
                  onChange={(e) => {
                    const newStatus = e.target.value;
                    updateApplicationStatus(
                      viewingFormSubmission.id,
                      newStatus
                    );

                    // Update the viewing submission state
                    setViewingFormSubmission({
                      ...viewingFormSubmission,
                      status: newStatus,
                    });

                    // If status changes to rejected and there's no note, start editing
                    if (
                      newStatus === "rejected" &&
                      !viewingFormSubmission.note
                    ) {
                      startEditingNote(viewingFormSubmission.id, "");
                    }
                  }}
                  className={`px-3 py-2 rounded-lg text-sm font-medium bg-slate-800 border border-slate-700 appearance-none cursor-pointer w-full
                  ${
                    viewingFormSubmission.status === "approved"
                      ? "text-green-400"
                      : viewingFormSubmission.status === "rejected"
                      ? "text-red-400"
                      : "text-yellow-400"
                  }`}
                >
                  <option
                    value="pending"
                    className="bg-slate-800 text-yellow-400"
                  >
                    Pending
                  </option>
                  <option
                    value="approved"
                    className="bg-slate-800 text-green-400"
                  >
                    Approved
                  </option>
                  <option
                    value="rejected"
                    className="bg-slate-800 text-red-400"
                  >
                    Rejected
                  </option>
                </select>
              </div>
            </div>

            <div className="p-6 max-h-[50vh] overflow-y-auto">
              {/* Metadata */}
              <div className="mb-6 p-4 bg-slate-800/50 rounded-lg">
                <p className="text-slate-400">
                  Submitted on:{" "}
                  {viewingFormSubmission.createdAt
                    ? new Date(
                        viewingFormSubmission.createdAt.seconds * 1000
                      ).toLocaleString()
                    : viewingFormSubmission.dateSubmitted
                    ? new Date(
                        viewingFormSubmission.dateSubmitted.seconds * 1000
                      ).toLocaleString()
                    : "Unknown date"}
                </p>
                <p className="text-slate-400">
                  Submitted by:{" "}
                  {viewingFormSubmission.submittedBy?.email || "Unknown"}
                </p>
              </div>

              {/* Direct access to elementOrder and elementMetadata at the top level */}
              <div className="space-y-4">
                {viewingFormSubmission.elementOrder &&
                  Array.isArray(viewingFormSubmission.elementOrder) &&
                  viewingFormSubmission.elementMetadata &&
                  viewingFormSubmission.elementOrder
                    // Filter out any headerImage elements
                    .filter((elementId) => {
                      const metadata =
                        viewingFormSubmission.elementMetadata[elementId];
                      return metadata && metadata.type !== "headerImage";
                    })
                    .map((elementId) => {
                      const metadata =
                        viewingFormSubmission.elementMetadata[elementId];
                      if (!metadata) return null;

                      const value =
                        viewingFormSubmission.responses?.[elementId];

                      return (
                        <div
                          key={elementId}
                          className="p-4 bg-slate-800/50 rounded-lg"
                        >
                          <label className="block text-sm font-medium text-slate-400 mb-1">
                            {metadata.label || elementId}
                          </label>

                          {/* Render field based on type - Skip headerImage */}
                          {metadata.type ===
                          "headerImage" ? null : metadata.type === // Don't render headerImage fields
                            "signature" ? (
                            <div>
                              {value ? (
                                <img
                                  src={value}
                                  alt="Signature"
                                  className="max-w-full h-auto border border-slate-700 rounded-lg"
                                />
                              ) : (
                                <div className="text-slate-500">
                                  No signature
                                </div>
                              )}
                            </div>
                          ) : metadata.type === "date" &&
                            value &&
                            typeof value === "object" &&
                            "seconds" in value ? (
                            <div className="text-white">
                              {new Date(
                                value.seconds * 1000
                              ).toLocaleDateString()}
                            </div>
                          ) : metadata.type === "yesNo" ? (
                            <div className="text-white">
                              {value === true
                                ? "Yes"
                                : value === false
                                ? "No"
                                : "Not specified"}
                            </div>
                          ) : metadata.type === "multipleChoice" &&
                            Array.isArray(value) ? (
                            <div className="text-white">
                              {value.length > 0 ? (
                                <ul className="list-disc pl-5">
                                  {value.map((item, i) => (
                                    <li key={i}>{item}</li>
                                  ))}
                                </ul>
                              ) : (
                                <span className="text-slate-500">
                                  No selection
                                </span>
                              )}
                            </div>
                          ) : (
                            <div className="text-white break-words">
                              {value !== undefined ? (
                                String(value)
                              ) : (
                                <span className="text-slate-500">
                                  No response
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
              </div>

              {/* Try accessing via responses.elementOrder as well */}
              {!viewingFormSubmission.elementOrder &&
                viewingFormSubmission.responses &&
                viewingFormSubmission.responses.elementOrder &&
                Array.isArray(viewingFormSubmission.responses.elementOrder) && (
                  <div className="space-y-4">
                    {viewingFormSubmission.responses.elementOrder
                      // Filter out any headerImage elements
                      .filter((elementId) => {
                        const metadata =
                          viewingFormSubmission.responses.elementMetadata?.[
                            elementId
                          ];
                        return metadata && metadata.type !== "headerImage";
                      })
                      .map((elementId) => {
                        const metadata =
                          viewingFormSubmission.responses.elementMetadata?.[
                            elementId
                          ];
                        if (!metadata) return null;

                        const value =
                          viewingFormSubmission.responses.responses?.[
                            elementId
                          ];

                        return (
                          <div
                            key={elementId}
                            className="p-4 bg-slate-800/50 rounded-lg"
                          >
                            <label className="block text-sm font-medium text-slate-400 mb-1">
                              {metadata.label || elementId}
                            </label>

                            {/* Render field based on type - Skip headerImage */}
                            {metadata.type ===
                            "headerImage" ? null : metadata.type === // Don't render headerImage fields
                              "signature" ? (
                              <div>
                                {value ? (
                                  <img
                                    src={value}
                                    alt="Signature"
                                    className="max-w-full h-auto border border-slate-700 rounded-lg"
                                  />
                                ) : (
                                  <div className="text-slate-500">
                                    No signature
                                  </div>
                                )}
                              </div>
                            ) : metadata.type === "date" &&
                              value &&
                              typeof value === "object" &&
                              "seconds" in value ? (
                              <div className="text-white">
                                {new Date(
                                  value.seconds * 1000
                                ).toLocaleDateString()}
                              </div>
                            ) : metadata.type === "yesNo" ? (
                              <div className="text-white">
                                {value === true
                                  ? "Yes"
                                  : value === false
                                  ? "No"
                                  : "Not specified"}
                              </div>
                            ) : metadata.type === "multipleChoice" &&
                              Array.isArray(value) ? (
                              <div className="text-white">
                                {value.length > 0 ? (
                                  <ul className="list-disc pl-5">
                                    {value.map((item, i) => (
                                      <li key={i}>{item}</li>
                                    ))}
                                  </ul>
                                ) : (
                                  <span className="text-slate-500">
                                    No selection
                                  </span>
                                )}
                              </div>
                            ) : (
                              <div className="text-white break-words">
                                {value !== undefined ? (
                                  String(value)
                                ) : (
                                  <span className="text-slate-500">
                                    No response
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                )}

              {/* Fallback if element ordering isn't available */}
              {!viewingFormSubmission.elementOrder &&
                (!viewingFormSubmission.responses ||
                  !viewingFormSubmission.responses.elementOrder ||
                  !Array.isArray(
                    viewingFormSubmission.responses.elementOrder
                  )) && (
                  <div className="text-center py-8">
                    <p className="text-slate-400">
                      Application data is available but cannot be displayed in
                      the proper format.
                    </p>
                    <p className="text-slate-500 mt-2">
                      Debugging information:
                    </p>
                    <div className="mt-4 text-left text-xs text-slate-500 bg-slate-800/50 p-4 rounded-lg overflow-x-auto">
                      <p>
                        elementOrder exists:{" "}
                        {viewingFormSubmission.elementOrder ? "Yes" : "No"}
                      </p>
                      <p>
                        responses exists:{" "}
                        {viewingFormSubmission.responses ? "Yes" : "No"}
                      </p>
                      <p>
                        responses.elementOrder exists:{" "}
                        {viewingFormSubmission.responses?.elementOrder
                          ? "Yes"
                          : "No"}
                      </p>
                      <p>Data structure:</p>
                      <pre className="mt-2 overflow-auto max-h-40">
                        {JSON.stringify(
                          viewingFormSubmission,
                          null,
                          2
                        ).substring(0, 1000)}
                        ...
                      </pre>
                    </div>
                  </div>
                )}
            </div>

            <div className="px-6 py-4 border-t border-slate-700 flex justify-end gap-3">
              <button
                onClick={() => moveToWaitlist(viewingFormSubmission)}
                className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white rounded-lg transition-colors flex items-center gap-2"
              >
                <ListPlus size={16} />
                Move to Waitlist
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Applications;
